/* eslint-disable @stylistic/max-len */
import type {RefObject} from 'react';

interface EnhanceProps {
    /**
     * A string representing the CSS class to be applied to the EnlargeIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#6E6E77'
     */
    color1?: string;
    /**
     * A number or string representing the height of the EnlargeIcon element.
     *
     * @default 32
     */
    height?: number | string;
    /**
     * A ref object to access the SVG element.
     */
    ref?: RefObject<SVGSVGElement | null>;
    testId?: string;
    /**
     * A number or string representing the height of the EnlargeIcon element.
     *
     * @default 32
     */
    width?: number | string;
}

/**
 * Renders a scalable vector graphic (SVG) representing an “Enlarge” icon, often used for actions such as
 * maximizing, zooming in, or expanding an element within the user interface.
 *
 * The icon is composed of symmetrical arrow-like shapes extending from the corners, visually indicating an
 * outward expansion motion. It supports customization through props such as `color1` for the stroke or fill color,
 * `width` and `height` for sizing, and `className` for styling purposes. The `ref` prop allows direct access to the
 * SVG element for manipulation or animations.
 *
 * This component is ideal for integration into toolbars, media controls, or image viewers where users can expand
 * content or view it in greater detail.
 *
 * @param props           The component props.
 * @param props.className Optional CSS class applied to the SVG for custom styling.
 * @param props.color1    The primary color of the icon’s fill or stroke. Defaults to black (`#000`).
 * @param props.height    The height of the SVG in pixels or other CSS units. Defaults to `32`.
 * @param props.ref       A React ref object for accessing the underlying SVG element directly.
 * @param props.testId    A unique identifier for testing, assigned to `data-cy`. Defaults to `'Enlarge'`.
 * @param props.width     The width of the SVG in pixels or other CSS units. Defaults to `32`.
 * @returns A JSX `<svg>` element rendering the enlarge icon with configurable color and size.
 *
 * @example
 * ```tsx
 * <Enlarge color1="#ffffff" width={48} height={48} className="icon-enlarge" />
 * ```
 */
export const Enlarge = ({
    className = '',
    color1 = '#000',
    height = 32,
    ref,
    testId = 'Enlarge',
    width = 32
}: EnhanceProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill="none"
        height={height}
        viewBox="0 0 32 32"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12.293 18.293C12.6835 17.9024 13.3165 17.9024 13.707 18.293C14.0975 18.6835 14.0975 19.3165 13.707 19.707L7.41406 26H11C11.5523 26 12 26.4477 12 27C12 27.5523 11.5523 28 11 28H5C4.94333 28 4.88808 27.9934 4.83398 27.9844C4.82358 27.9826 4.81309 27.9816 4.80273 27.9795C4.79123 27.9772 4.77991 27.9744 4.76855 27.9717C4.74799 27.9668 4.72732 27.9623 4.70703 27.9561C4.65838 27.9412 4.61159 27.9222 4.56641 27.9004C4.52662 27.8812 4.48768 27.8597 4.4502 27.835C4.31255 27.7441 4.19887 27.6203 4.12012 27.4746C4.11269 27.4609 4.10635 27.4466 4.09961 27.4326C4.03652 27.3016 4 27.1552 4 27V21C4 20.4477 4.44772 20 5 20C5.55228 20 6 20.4477 6 21V24.5859L12.293 18.293Z"
            fill={color1}
        />
        <path
            d="M18.293 18.293C18.6835 17.9024 19.3165 17.9024 19.707 18.293L26 24.5859V21C26 20.4477 26.4477 20 27 20C27.5523 20 28 20.4477 28 21V27C28 27.083 27.9878 27.1631 27.9688 27.2402C27.9646 27.2572 27.9611 27.2743 27.9561 27.291C27.9412 27.34 27.9223 27.3871 27.9004 27.4326C27.8804 27.4743 27.8572 27.5146 27.8311 27.5537C27.7567 27.6651 27.6616 27.7612 27.5498 27.835C27.5121 27.8599 27.4727 27.8811 27.4326 27.9004C27.3871 27.9223 27.34 27.9412 27.291 27.9561C27.2743 27.9611 27.2572 27.9646 27.2402 27.9688C27.2246 27.9726 27.2092 27.9774 27.1934 27.9805C27.1863 27.9819 27.179 27.9822 27.1719 27.9834C27.1159 27.9931 27.0587 28 27 28H21C20.4477 28 20 27.5523 20 27C20 26.4477 20.4477 26 21 26H24.5859L18.293 19.707C17.9024 19.3165 17.9024 18.6835 18.293 18.293Z"
            fill={color1}
        />
        <path
            d="M11.1025 4.00488C11.6067 4.05621 12 4.48232 12 5C12 5.51768 11.6067 5.94379 11.1025 5.99512L11 6H7.41406L13.707 12.293C14.0975 12.6835 14.0975 13.3165 13.707 13.707C13.3165 14.0975 12.6835 14.0975 12.293 13.707L6 7.41406V11L5.99512 11.1025C5.94379 11.6067 5.51768 12 5 12C4.48232 12 4.05621 11.6067 4.00488 11.1025L4 11V5L4.00488 4.89746C4.01771 4.77152 4.05419 4.65263 4.10938 4.54492L4.11426 4.53516C4.11825 4.52755 4.12275 4.52021 4.12695 4.5127C4.20394 4.37517 4.31209 4.25757 4.44238 4.16992C4.44628 4.1673 4.45017 4.16467 4.4541 4.16211C4.47474 4.14864 4.49592 4.13597 4.51758 4.12402C4.52486 4.12001 4.5317 4.11515 4.53906 4.11133C4.55969 4.10061 4.58111 4.09132 4.60254 4.08203C4.61098 4.07838 4.61941 4.0747 4.62793 4.07129C4.63785 4.06731 4.64813 4.06421 4.6582 4.06055C4.69224 4.04819 4.72678 4.03784 4.76172 4.0293C4.83814 4.01061 4.91783 4 5 4H11L11.1025 4.00488Z"
            fill={color1}
        />
        <path
            d="M27.1025 4.00488C27.2297 4.01783 27.3495 4.05524 27.458 4.11133C27.4676 4.11627 27.4769 4.12169 27.4863 4.12695C27.5636 4.1701 27.6344 4.22288 27.6973 4.28418C27.7004 4.28721 27.704 4.28989 27.707 4.29297C27.7098 4.29574 27.7121 4.29896 27.7148 4.30176C27.7775 4.36595 27.8313 4.43844 27.875 4.51758C27.879 4.52487 27.8839 4.53169 27.8877 4.53906C27.8984 4.5597 27.9077 4.58109 27.917 4.60254C27.9338 4.64124 27.9483 4.68058 27.96 4.7207C27.9857 4.80939 28 4.90302 28 5V11C28 11.5523 27.5523 12 27 12C26.4477 12 26 11.5523 26 11V7.41406L19.707 13.707C19.3165 14.0975 18.6835 14.0975 18.293 13.707C17.9024 13.3165 17.9024 12.6835 18.293 12.293L24.5859 6H21C20.4477 6 20 5.55228 20 5C20 4.44772 20.4477 4 21 4H27L27.1025 4.00488Z"
            fill={color1}
        />
    </svg>
);